import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import type { BillingEntityType, FiscalRegime } from '../../models/billing.model';

const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

/** Strip spaces, dots and hyphens; uppercase. */
export function normalizeTaxId(raw: string): string {
  return raw.replace(/[\s.-]/g, '').toUpperCase();
}

export function isValidDniNie(id: string): boolean {
  const v = normalizeTaxId(id);
  let number: number;
  let letter: string;

  if (/^\d{8}[A-Z]$/.test(v)) {
    number = Number.parseInt(v.slice(0, 8), 10);
    letter = v[8];
  } else if (/^[XYZ]\d{7}[A-Z]$/.test(v)) {
    const prefix: Record<string, string> = { X: '0', Y: '1', Z: '2' };
    number = Number.parseInt(prefix[v[0]] + v.slice(1, 8), 10);
    letter = v[8];
  } else {
    return false;
  }

  if (!Number.isFinite(number)) return false;
  return DNI_LETTERS[number % 23] === letter;
}

/** Spanish CIF control digit (empresas). */
export function isValidCif(cif: string): boolean {
  const v = normalizeTaxId(cif);
  if (!/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(v)) return false;

  const digits = v.slice(1, 8);
  const control = v[8];
  const first = v[0];

  let sumEven = 0;
  let sumOdd = 0;
  for (let i = 0; i < digits.length; i++) {
    const n = Number.parseInt(digits[i], 10);
    if (i % 2 === 0) {
      const dbl = n * 2;
      sumOdd += dbl > 9 ? dbl - 9 : dbl;
    } else {
      sumEven += n;
    }
  }
  const controlDigit = (10 - ((sumEven + sumOdd) % 10)) % 10;
  const controlLetter = 'JABCDEFGHI'[controlDigit];

  if (/[ABEH]/.test(first)) {
    return control === String(controlDigit);
  }
  if (/[KPQS]/.test(first)) {
    return control === controlLetter;
  }
  return control === String(controlDigit) || control === controlLetter;
}

export function isValidTaxIdForRegime(taxId: string, regime: FiscalRegime): boolean {
  const v = normalizeTaxId(taxId);
  if (!v) return false;
  if (regime === 'particular') {
    return isValidDniNie(v);
  }
  return isValidDniNie(v) || isValidCif(v);
}

export function spanishTaxIdValidator(regime: () => FiscalRegime): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return null;
    return isValidTaxIdForRegime(raw, regime()) ? null : { spanishTaxId: true };
  };
}

export function isValidTaxIdForEntityType(
  taxId: string,
  entityType: BillingEntityType,
): boolean {
  const v = normalizeTaxId(taxId);
  if (!v) return false;
  return entityType === 'persona_juridica' ? isValidCif(v) : isValidDniNie(v);
}

export function spanishTaxIdForEntityValidator(
  entityType: () => BillingEntityType,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return null;
    return isValidTaxIdForEntityType(raw, entityType())
      ? null
      : { spanishTaxId: true };
  };
}
